import type { AnchorHref } from './types'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { pdpUseCases } from '@/scopes/products/domain/useCases'
import { pdpStore } from '@/scopes/products/infrastructure/store'
import { tk } from '@sunwebgroup/ui-components'
import { type ReactNode, useRef } from 'react'
import { styled } from 'styled-components'

const ANCHOR_SELECTION_CLASS = 'anchorTarget'

interface AnchorTargetProps {
  anchorHref: AnchorHref
  children: ReactNode
}

const { onAnchorTargetIntersects } = pdpUseCases({ store: pdpStore })

const Target = styled.div`
  scroll-margin-top: ${tk('spacing_5xl')};
`

export default function AnchorTarget({ anchorHref, children }: AnchorTargetProps) {
  const ref = useRef<HTMLDivElement>(null)
  useOnScreen(ref, {
    threshold: [0, 0.05, 0.1, 0.2, 0.5, 0.8, 0.9, 0.95, 1],
    onIntersect: ({ entry }) => {
      onAnchorTargetIntersects({
        anchorHref,
        isIntersecting: entry.isIntersecting,
        anchorTopPositions: getAnchorsTopPositions(),
      })
    },
  })

  return (
    <Target ref={ref} id={anchorHref} className={ANCHOR_SELECTION_CLASS}>
      {children}
    </Target>
  )
}

function getAnchorsTopPositions(): Record<string, number> {
  return Object.fromEntries(
    Array.from(document.querySelectorAll<HTMLDivElement>(`.${ANCHOR_SELECTION_CLASS}`)).map(anchorElement => [
      anchorElement.id,
      anchorElement.getBoundingClientRect().top,
    ]),
  )
}
